import React, { useState } from "react";
import { Container, FlexContainer } from "src/design-system/Container";
import { Grid, GridRowSmallItems } from "src/design-system/Tokens/grid";
import { H2 } from "src/design-system/Tokens/typography";
import { IconSize } from "src/design-system/Tokens/icons";
import { NavigationButton } from "src/design-system/NavigationButton";
import { Select } from "src/design-system/Select";
import { DropdownStyle, ISelectObject } from "src/design-system/Select/Select";
import { ToolSettingIcon } from "src/icons";
import { translateString } from "src/util/localization";
import { getPathWithParams, getRoutes } from "src/routes";
import { UnplannedRequestFiltersComponent, UnplannedRequestListComponent } from "./unplannedRequest.components";
import { useUnplannedRequestFiltersStateProps } from "./unplannedRequest.hooks";
import { ResponsiveViewContainer, StickyFiltersContainer } from "./unplannedRequest.styles";
import { DEFAULT_SORT_BY, DEFAULT_SORT_ORDER, getSortOptions } from "./unplannedRequest.utils";
import { UnplannedRequestFilters, UnplannedRequestSortOption } from "./unplannedRequest.types";
import { Branch } from "src/views/NewUnplannedRequestView/Branches";

const DEFAULT_FILTERS: UnplannedRequestFilters = {
  installation: [],
  equipment: [],
  requestType: [],
  status: [],
  submittedDateFrom: "",
  submittedDateTo: "",
  customerPONumber: "",
  sortBy: DEFAULT_SORT_BY,
  sortOrder: DEFAULT_SORT_ORDER,
};

export const UnplannedRequestsView = () => {
  const [filters, setFilters] = useState<UnplannedRequestFilters>(DEFAULT_FILTERS);
  const [isFiltersTouched, setIsFiltersTouched] = useState<boolean>(false);

  const setSingleFilter = <T extends keyof UnplannedRequestFilters>(key: T, value: UnplannedRequestFilters[T]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    if (!isFiltersTouched) setIsFiltersTouched(true);
  };

  const sortOptions = getSortOptions();
  const routes = getRoutes();
  const { installations } = useUnplannedRequestFiltersStateProps();

  const singleSelectedInstallationId = filters.installation?.length === 1 ? filters.installation[0] : undefined;

  const resolvedInstallationId = singleSelectedInstallationId
    ? installations.find((inst) => inst.salesforceId === singleSelectedInstallationId)?.id
    : undefined;

  const getCreateRequestHref = (requestType: Branch) => {
    const pathname = getPathWithParams(routes.CreateUnplannedRequest, { phase: 2, requestType });
    return {
      pathname,
      state: resolvedInstallationId ? { selectedInstallationId: resolvedInstallationId } : {},
    };
  };

  return (
    <Container $fullwidth>
      <Container $fullwidth>
        <Grid itemsPerRow={GridRowSmallItems} fallbackWidth={{ min: 125, max: 140 }}>
          <NavigationButton
            icon={<ToolSettingIcon size={IconSize.Large} />}
            href={getCreateRequestHref(Branch.SparePartReplenishment)}
            label={translateString("contracts.unplannedRequests.newSparePartReplenishmentRequest")}
          />
        </Grid>
      </Container>

      <ResponsiveViewContainer $fullwidth $marginTop={4}>
        <FlexContainer>
          <StickyFiltersContainer>
            <UnplannedRequestFiltersComponent filters={filters} setSingleFilter={setSingleFilter} />
          </StickyFiltersContainer>
        </FlexContainer>

        <Container $fullwidth>
          <FlexContainer $spaceBetween $marginBottom={1}>
            <H2>{translateString("contracts.unplannedRequests.title")}</H2>
            <FlexContainer>
              <Select
                placeholder={translateString("select.sortPlaceholder")}
                options={sortOptions}
                value={sortOptions.find((so) => so.sortBy === filters.sortBy && so.sortOrder === filters.sortOrder)}
                onChange={(option: UnplannedRequestSortOption) => {
                  setSingleFilter("sortBy", option.sortBy);
                  setSingleFilter("sortOrder", option.sortOrder);
                }}
                dropdownStyle={DropdownStyle.Toolbar}
                getOptionLabel={(option: ISelectObject<UnplannedRequestSortOption>) => option.item.label}
              />
            </FlexContainer>
          </FlexContainer>
          <UnplannedRequestListComponent filters={filters} showPreselected={!isFiltersTouched} />
        </Container>
      </ResponsiveViewContainer>
    </Container>
  );
};
